import React from 'react';

import GreyPlus from 'icons/add-grey.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CircleQuestion from 'components/application/CircleQuestion';
import RowBar from 'components/application/RowBar';
import Tooltip from 'components/application/Tooltip';

import AddButton from 'components/buttons/AddButton';
import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import ToggleableSelect from 'components/selectors/ToggleableSelect';

import { sendAnalytics } from 'components/helpers/analytics';
import { isCourseAssociated } from 'components/helpers/resources/courses';

export default function AdditionalTrainingBar({
  actionButtonsVisible = true,
  additionalCoursesCount,
  coursesMeta,
  isLoading = false,
  onCourseSelectChange,
  personnelId,
  roleHasCourses = true,
  roles = [],
  training = [],
  userCourses = [],
}) {
  const breadBoard = useBreadBoard();
  const { user: currentUser } = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'training.AdditionalTrainingBar',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const handleChange = (selected) => {
    onCourseSelectChange({ selected });
  };

  const shouldConsiderRoleContext = roleHasCourses || roles.length > 0;

  const content =
    additionalCoursesCount > 0 ?
      <>
        <span className='tw-mr-2 tw-text-l tw-font-semibold'>
          {shouldConsiderRoleContext ?
            i18nTCommon('training.trainingOutsideThisRole')
          : i18nTCommon('shared.training')}
        </span>
        <Tooltip placement='top' tooltip={i18nT('tooltip')} trigger='hover'>
          <CircleQuestion />
        </Tooltip>
      </>
    : <div className='-tw-ml-1 tw-flex tw-items-center'>
        <GreyPlus className='[&_rect]:tw-fill-grey-300' />
        <span className='tw-ml-3'>
          {shouldConsiderRoleContext ?
            i18nT('emptyState.additionalTraining')
          : i18nT('emptyState.training')}
        </span>
      </div>;

  const disabledBtn = actionButtonsVisible && (
    <AddButton
      disabled={true}
      text={i18nTCommon('training.addTrainingCourse')}
      tooltipText={!isLoading && i18nT('disabledTooltip')}
    />
  );

  const addCourseToggle = actionButtonsVisible && (
    <ToggleableSelect
      additionalNoOptionsMessage={i18nTCommon(
        'courses.newCoursesMustBeAddedFromCourseRegister',
      )}
      buttonText={i18nTCommon('training.addTrainingCourse')}
      isOptionDisabled={(course) =>
        isCourseAssociated(course, { training, roles, userCourses })
      }
      name='course'
      onChange={handleChange}
      onError={breadBoard.addInedibleToast}
      onToggleablePrimaryClick={() =>
        sendAnalytics('Add training course clicked', {
          currentUser,
          personnelId,
        })
      }
      placeholder={i18nT('select.placeholder')}
      url='/account_courses'
    />
  );

  return (
    <RowBar
      actions={
        coursesMeta.info.totalCount > 0 && !isLoading ?
          addCourseToggle
        : disabledBtn
      }
      content={!isLoading && content}
      modifiers={
        !isLoading && additionalCoursesCount > 0 ?
          ['large', 'border-bottom-none']
        : ['large']
      }
    />
  );
}

AdditionalTrainingBar.propTypes = {
  training: PropTypes.array.isRequired,
  userCourses: PropTypes.array.isRequired,
  onCourseSelectChange: PropTypes.func.isRequired,
  roleHasCourses: PropTypes.bool.isRequired,
};
